import React, { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import styled from 'styled-components/native'
import { useMutation } from '@apollo/client'
import Modal from '../../components/common/Modal'
import Button from '../../components/common/Button'
import useTranslation from '../../hooks/useTranslation'
import { deleteShareDocletMutation, isNotFoundError } from './api'
import { builderColors } from './builder/builderColors'

export interface DocletToDelete {
  id: string
  displayName: string | null
  isPublished: boolean
}

interface DeleteDocletDialogProps {
  // The doclet to confirm deleting; null keeps the dialog closed
  doclet: DocletToDelete | null
  onCancel: () => void
  /**
   * The doclet is gone: deleted now, or by someone else already
   * (`alreadyDeleted`), which counts as done rather than as a failure
   */
  onDeleted: (alreadyDeleted: boolean) => void
}

/**
 * "Delete this doclet?", from the Doclets list and from Document settings.
 * Deleting is a soft delete on the API (deleteShareDoclet), which also takes
 * a published doclet's share link offline, so the dialog says so. There's no
 * undo in MI6.
 */
const DeleteDocletDialog = ({
  doclet,
  onCancel,
  onDeleted
}: DeleteDocletDialogProps) => {
  const { t } = useTranslation()
  const [deleteDoclet] = useMutation(deleteShareDocletMutation)
  const [deleting, setDeleting] = useState(false)
  const [failed, setFailed] = useState(false)

  // Each opening starts afresh
  useEffect(() => {
    if (doclet) {
      setDeleting(false)
      setFailed(false)
    }
  }, [doclet])

  const confirm = async () => {
    if (!doclet || deleting) return
    setDeleting(true)
    setFailed(false)
    try {
      await deleteDoclet({
        variables: { id: doclet.id },
        // Drop it from the cache, so nothing shows it from there
        update: cache => {
          cache.evict({
            id: cache.identify({ __typename: 'ShareDoclet', id: doclet.id })
          })
          cache.gc()
        }
      })
      onDeleted(false)
    } catch (error) {
      if (isNotFoundError(error)) {
        onDeleted(true)
      } else {
        setFailed(true)
        setDeleting(false)
      }
    }
  }

  const name = doclet?.displayName || t('doclets:untitled')

  return (
    <Modal
      open={!!doclet}
      close={() => !deleting && onCancel()}
      bodyStyle={{ padding: 24, maxWidth: 480 }}
    >
      <Title>{t('doclets:delete:title')}</Title>
      <Body>
        {t(
          doclet?.isPublished
            ? 'doclets:delete:bodyPublished'
            : 'doclets:delete:bodyDraft',
          { name }
        )}
      </Body>
      {failed && <ErrorText>{t('doclets:delete:failed')}</ErrorText>}
      <ButtonRow>
        <Button
          label={t('doclets:delete:cancel')}
          onPress={onCancel}
          disabled={deleting}
          buttonStyle={{
            backgroundColor: '#FFFFFF',
            borderWidth: 1,
            borderColor: builderColors.border
          }}
          titleStyle={{ color: builderColors.text }}
        />
        <Button
          label={t(
            deleting ? 'doclets:delete:deleting' : 'doclets:delete:confirm'
          )}
          onPress={confirm}
          disabled={deleting}
          style={{ marginLeft: 8 }}
          buttonStyle={{ backgroundColor: builderColors.danger }}
        />
      </ButtonRow>
    </Modal>
  )
}

/** Someone else deleted the doclet while this editor had it open. */
export const DocletDeletedModal = ({
  open,
  onBack
}: {
  open: boolean
  onBack: () => void
}) => {
  const { t } = useTranslation()
  return (
    <Modal
      open={open}
      close={() => {}}
      bodyStyle={{ padding: 24, maxWidth: 480 }}
    >
      <Title>{t('doclets:delete:deletedElsewhereTitle')}</Title>
      <Body>{t('doclets:delete:deletedElsewhereBody')}</Body>
      <ButtonRow>
        <Button label={t('doclets:delete:backToList')} onPress={onBack} />
      </ButtonRow>
    </Modal>
  )
}

const Title = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${builderColors.text};
  margin-bottom: 8px;
`

const Body = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  color: ${builderColors.text};
  margin-bottom: 20px;
`

const ErrorText = styled(Text)`
  font-size: 13px;
  line-height: 18px;
  color: ${builderColors.danger};
  margin-top: -8px;
  margin-bottom: 16px;
`

const ButtonRow = styled(View)`
  flex-direction: row;
  justify-content: flex-end;
`

export default DeleteDocletDialog
